import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  getApiPartnersByIdContractsOptions,
  getApiPartnersByIdCounterpartiesOptions,
  getApiPartnersByIdDeliveryAddressOptions,
  getApiPartnersByIdLastContractOptions,
  getApiPartnersByIdLastPricesOptions,
  getApiPartnersByIdLastWarehouseOptions,
  getApiPartnersOptions,
  getApiProductsOptions,
  getApiWarehousesByIdAvailableStockOptions,
  getApiWarehousesOptions,
  type AvailableStockDto,
  type CounterpartyContractDto,
  type CounterpartyDto,
  type DeliveryAddressHintDto,
  type GetApiPartnersByIdContractsError,
  type GetApiPartnersByIdCounterpartiesError,
  type GetApiPartnersByIdDeliveryAddressError,
  type GetApiPartnersByIdLastContractError,
  type GetApiPartnersByIdLastPricesError,
  type GetApiPartnersByIdLastWarehouseError,
  type GetApiPartnersError,
  type GetApiProductsError,
  type GetApiWarehousesByIdAvailableStockError,
  type GetApiWarehousesError,
  type LastContractDto,
  type LastPriceDto,
  type LastWarehouseDto,
  type PageOfPartnerDto,
  type PageOfProductDto,
  type WarehouseDto,
} from '@/api';

/*
 * Справочные запросы формы отгрузки: поиск клиента и товара, контрагент и договоры
 * клиента, склады и подсказки - прошлая цена, склад и договор прошлого заказа,
 * адрес доставки и свободный остаток.
 * Типы возврата заданы явно (TS2742).
 */

/** Сколько вариантов показывает поле выбора: дальше менеджер уточняет поиск. */
const OPTIONS_LIMIT = 20;

/** Действующие клиенты по подстроке кода или наименования. */
export const usePartnerOptions = (
  search: string,
): UseQueryResult<PageOfPartnerDto, GetApiPartnersError> =>
  useQuery({
    ...getApiPartnersOptions({
      query: {
        Search: search === '' ? undefined : search,
        PageSize: OPTIONS_LIMIT,
      },
    }),
    placeholderData: (previous) => previous,
  });

/** Действующие товары по подстроке артикула или наименования. */
export const useProductOptions = (
  search: string,
): UseQueryResult<PageOfProductDto, GetApiProductsError> =>
  useQuery({
    ...getApiProductsOptions({
      query: {
        Search: search === '' ? undefined : search,
        PageSize: OPTIONS_LIMIT,
      },
    }),
    placeholderData: (previous) => previous,
  });

/** Действующие контрагенты клиента: сервер подставит единственного из них. */
export const usePartnerCounterparties = (
  partnerId: string | undefined,
): UseQueryResult<
  Array<CounterpartyDto>,
  GetApiPartnersByIdCounterpartiesError
> =>
  useQuery({
    ...getApiPartnersByIdCounterpartiesOptions({
      path: { id: partnerId ?? '' },
    }),
    enabled: partnerId !== undefined,
  });

/**
 * Товары для запроса подсказки: каждый по разу и в одном порядке. Повтор товара в
 * строках удлинял бы строку запроса - а она ограничена (не больше 100 товаров, см.
 * QueryStringLimits в бэкенде), - порядок делает ключ кеша устойчивым.
 */
const distinctSorted = (productIds: ReadonlyArray<string>): Array<string> =>
  [...new Set(productIds)].sort();

/**
 * Договоры клиента, годные для заказа: с клиентом, действующие в 1С, на его
 * контрагента. Договор без банковского счёта в списке есть - с признаком.
 */
export const usePartnerContracts = (
  partnerId: string | undefined,
): UseQueryResult<
  Array<CounterpartyContractDto>,
  GetApiPartnersByIdContractsError
> =>
  useQuery({
    ...getApiPartnersByIdContractsOptions({ path: { id: partnerId ?? '' } }),
    enabled: partnerId !== undefined,
  });

/**
 * Договор, который форма подставляет, пока менеджер не выбрал свой: прошлого
 * заказа клиента с этого склада, без него - прошлого заказа, без него -
 * единственный договор.
 */
export const useLastContract = (
  partnerId: string | undefined,
  warehouseId: string | undefined,
): UseQueryResult<LastContractDto, GetApiPartnersByIdLastContractError> =>
  useQuery({
    ...getApiPartnersByIdLastContractOptions({
      path: { id: partnerId ?? '' },
      query: { warehouseId },
    }),
    enabled: partnerId !== undefined,
  });

/**
 * Прошлая цена клиента по выбранным товарам - из копии заказов 1С.
 *
 * Предыдущий ответ на время перезапроса не держим: при смене клиента на экране
 * мелькнули бы цены чужого клиента.
 */
export const useLastPrices = (
  partnerId: string | undefined,
  productIds: ReadonlyArray<string>,
): UseQueryResult<Array<LastPriceDto>, GetApiPartnersByIdLastPricesError> =>
  useQuery({
    ...getApiPartnersByIdLastPricesOptions({
      path: { id: partnerId ?? '' },
      query: { productIds: distinctSorted(productIds) },
    }),
    enabled: partnerId !== undefined && productIds.length > 0,
  });

/**
 * Свободный остаток выбранных товаров на складе отправки - из копии остатков 1С
 * (architecture/contracts/onec-stock.md). Строки формы ещё не сохранены, а склад
 * менеджер может сменить, поэтому остаток спрашивается у склада, а не у карточки.
 *
 * Предыдущий ответ на время перезапроса не держим: при смене склада на экране
 * мелькнул бы остаток другого склада.
 */
export const useAvailableStock = (
  warehouseId: string | undefined,
  productIds: ReadonlyArray<string>,
): UseQueryResult<
  Array<AvailableStockDto>,
  GetApiWarehousesByIdAvailableStockError
> =>
  useQuery({
    ...getApiWarehousesByIdAvailableStockOptions({
      path: { id: warehouseId ?? '' },
      query: { productIds: distinctSorted(productIds) },
    }),
    enabled: warehouseId !== undefined && productIds.length > 0,
  });

/** Действующие склады по наименованию - склад отправки выбирает менеджер. */
export const useWarehouses = (): UseQueryResult<
  Array<WarehouseDto>,
  GetApiWarehousesError
> => useQuery(getApiWarehousesOptions());

/** Склад прошлого заказа клиента - подсказка, пока менеджер склад не выбрал. */
export const useLastWarehouse = (
  partnerId: string | undefined,
): UseQueryResult<LastWarehouseDto, GetApiPartnersByIdLastWarehouseError> =>
  useQuery({
    ...getApiPartnersByIdLastWarehouseOptions({
      path: { id: partnerId ?? '' },
    }),
    enabled: partnerId !== undefined,
  });

/**
 * Адрес доставки по умолчанию - адрес последнего заказа клиента, без него -
 * адрес клиента из 1С; подсказка, пока менеджер поле не трогал.
 */
export const useDeliveryAddressHint = (
  partnerId: string | undefined,
): UseQueryResult<
  DeliveryAddressHintDto,
  GetApiPartnersByIdDeliveryAddressError
> =>
  useQuery({
    ...getApiPartnersByIdDeliveryAddressOptions({
      path: { id: partnerId ?? '' },
    }),
    enabled: partnerId !== undefined,
  });
